import { epochMs } from "./time.js";
import type { ProgressItem } from "./types.js";

/** -1, 0 or 1: how `a` orders against `b` as instants (an unreadable clock is the oldest). */
export function compareUpdatedAt(a: string, b: string): -1 | 0 | 1 {
  const left = epochMs(a);
  const right = epochMs(b);
  const l = Number.isNaN(left) ? Number.NEGATIVE_INFINITY : left;
  const r = Number.isNaN(right) ? Number.NEGATIVE_INFINITY : right;
  if (l === r) return 0;
  return l < r ? -1 : 1;
}

/**
 * Facts that happen once keep their first time: an enrolment and a badge are written with
 * their own time as the clock, so the earlier of two devices' records is the true one.
 */
export const isEarliestWins = (key: string): boolean =>
  key.startsWith("track:") || key.startsWith("badge:");

const beats = (incoming: ProgressItem, held: ProgressItem): boolean =>
  isEarliestWins(incoming.key)
    ? compareUpdatedAt(incoming.updatedAt, held.updatedAt) < 0
    : compareUpdatedAt(incoming.updatedAt, held.updatedAt) > 0;

/**
 * Last-writer-wins per item, the reference the edge's SQL and the device's
 * mirror both follow: the newer `updatedAt` replaces the older, an older one is
 * ignored, and an equal one keeps the stored item, so a retried flush after a
 * killed app changes nothing. Items merge by key, never as a document.
 * Returns a new array sorted by key; neither input is touched.
 */
export function mergeProgress(
  stored: readonly ProgressItem[],
  incoming: readonly ProgressItem[],
): ProgressItem[] {
  const byKey = new Map<string, ProgressItem>();
  for (const item of stored) {
    const held = byKey.get(item.key);
    if (held === undefined || beats(item, held)) {
      byKey.set(item.key, item);
    }
  }
  for (const item of incoming) {
    const held = byKey.get(item.key);
    if (held === undefined || beats(item, held)) {
      byKey.set(item.key, item);
    }
  }
  return [...byKey.values()].toSorted((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}
